import { memo, useCallback, useEffect, useRef, useState } from "react";

import { cn } from "@/lib/chillnn/cn";

import { Dot } from "@/components/ui/dot";

import {
	FeatureIslandCategoryContext,
	useFeatureIslandCategoryContext,
	useFeatureIslandCategoryManagerContext,
} from "../../context";

/**
 * モバイル向けの横スクロール可能なカテゴリタブバー。
 * laptop 未満の画面サイズでのみ表示される想定。
 */
export const FeatureIslandCategoryTabBarForMobile = () => {
	const basicSetupItems = useFeatureIslandCategoryManagerContext((state) => state.basicSetupItems);
	const nonBasicSetupItems = useFeatureIslandCategoryManagerContext((state) => state.nonBasicSetupItems);
	const setActiveCategoryID = useFeatureIslandCategoryManagerContext((state) => state.setActiveCategoryID);

	const canShowAdditionalSetup = nonBasicSetupItems?.length > 0;

	const navRef = useRef<HTMLElement>(null);
	const [canScrollLeft, setCanScrollLeft] = useState(false);
	const [canScrollRight, setCanScrollRight] = useState(false);

	const updateScrollState = useCallback(() => {
		const el = navRef.current;
		if (!el) return;
		setCanScrollLeft(el.scrollLeft > 0);
		setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
	}, []);

	useEffect(() => {
		updateScrollState();
		const el = navRef.current;
		if (!el) return;
		const observer = new ResizeObserver(updateScrollState);
		observer.observe(el);
		return () => observer.disconnect();
	}, [updateScrollState]);

	return (
		<div className="sticky top-0 z-40 border-b border-zinc-200 bg-white">
			<div className="relative">
				<nav
					ref={navRef}
					onScroll={updateScrollState}
					className="flex gap-x-1 overflow-x-auto px-4 hidden-scroll-bar"
				>
					{basicSetupItems.map((item) => {
						const { categoryID } = item.getState();
						return (
							<FeatureIslandCategoryContext.Provider key={categoryID} value={item}>
								<TabItem onSetActiveCategoryID={setActiveCategoryID} />
							</FeatureIslandCategoryContext.Provider>
						);
					})}
					{canShowAdditionalSetup && (
						<>
							<div className="mx-1 my-3 w-px shrink-0 bg-zinc-200" />
							{nonBasicSetupItems.map((item) => {
								const { categoryID } = item.getState();
								return (
									<FeatureIslandCategoryContext.Provider key={categoryID} value={item}>
										<TabItem onSetActiveCategoryID={setActiveCategoryID} />
									</FeatureIslandCategoryContext.Provider>
								);
							})}
						</>
					)}
					{/* 最後のタブがスクロール時に切れないようにするスペーサー */}
					<div className="w-4 shrink-0" />
				</nav>

				{/* 左端グラデーション */}
				<div
					className={cn(
						"pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-white to-transparent transition-opacity duration-200",
						canScrollLeft ? "opacity-100" : "opacity-0",
					)}
				/>
				{/* 右端グラデーション */}
				<div
					className={cn(
						"pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-white to-transparent transition-opacity duration-200",
						canScrollRight ? "opacity-100" : "opacity-0",
					)}
				/>
			</div>
		</div>
	);
};

type TabItemProps = {
	onSetActiveCategoryID: (categoryID: string) => void;
};

const TabItem = memo(({ onSetActiveCategoryID }: TabItemProps) => {
	const categoryID = useFeatureIslandCategoryContext((state) => state.categoryID);
	const title = useFeatureIslandCategoryContext((state) => state.title);
	const isEdited = useFeatureIslandCategoryContext((state) => state.isEdited);
	const hasError = useFeatureIslandCategoryContext((state) => state.hasError);
	const activeCategoryID = useFeatureIslandCategoryManagerContext((state) => state.activeCategoryID);

	const isActive = activeCategoryID === categoryID;
	const ref = useRef<HTMLButtonElement>(null);

	useEffect(() => {
		if (isActive && ref.current) {
			ref.current.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
		}
	}, [isActive]);

	const handleClick = useCallback(() => onSetActiveCategoryID(categoryID), [onSetActiveCategoryID, categoryID]);

	return (
		<button
			ref={ref}
			type="button"
			onClick={handleClick}
			className={cn(
				"relative shrink-0 whitespace-nowrap px-3 py-5 text-sm text-zinc-500 transition-colors hover:text-zinc-900",
				isActive && "font-semibold text-zinc-900",
			)}
		>
			<span className="flex items-center gap-x-1.5">
				{title}
				{isEdited && <Dot />}
				{hasError && <Dot variant="red" />}
			</span>
			{isActive && <span className="absolute inset-x-0 bottom-0 h-0.5 bg-brand" />}
		</button>
	);
});

TabItem.displayName = "TabItem";
